<template>
  <content-dialog
    v-model="dialogOpen"
    title="Add Inventory Prefills"
    width="900px"
    height="80vh"
    fullscreen-tablet
    no-backdrop-dismiss
    @hide="hide"
  >
    <template #toolbar-buttons>
      <q-btn
        size="12px"
        rounded
        outline
        no-caps
        icon-right="mdi-package-variant-plus"
        :label="windowWidth < 600 ? 'Create' : 'Create New'"
        :dense="windowWidth < 600"
        @click="hide(), $emit('createNew')"
      />
    </template>

    <page-banner
      dense
      class="q-mx-sm"
      label="Prefill common inventory and chemicals into your business inventory collection. These items can then be used on applicable forms."
    />

    <page-banner
      v-if="!company.industry_settings.setup_prefill_inventory"
      dense
      dark
      color="red-8"
      class="q-mx-sm q-mt-xs"
      label="The prefills displayed here may not be relevant to your industry."
    />

    <q-toolbar>
      <q-input
        v-model="filterVals.search"
        :debounce="750"
        placeholder="Search Prefills..."
        dense
        outlined
        clearable
        class="q-mr-sm"
        style="min-width: 225px;"
      >
        <template #prepend>
          <q-icon name="mdi-magnify" />
        </template>
      </q-input>

      <q-select
        v-if="!!options && options.system_types"
        v-model="filterVals.system_types"
        :options="options.system_types"
        map-options
        emit-value
        multiple
        option-value="id"
        option-label="name"
        use-chips
        dense
        outlined
        clearable
        behavior="menu"
        label="Filter by category"
        style="min-width: 200px;"
        class="gt-xs"
        @clear="filterVals.system_types = []"
      >
        <template #option="scope">
          <q-select-color-item
            v-bind="scope.itemProps"
            :label="scope.opt.name"
            :color="scope.opt.color"
            type="checkbox"
            hex
          />
        </template>

        <template v-if="filterVals.system_types.length > 0" #selected>
          {{ filterVals.system_types.length }} {{ pluraliseString('option', filterVals.system_types.length) }}
        </template>
      </q-select>

      <q-space />

      <q-item-label class="q-pl-sm">
        {{ count }} results
      </q-item-label>
    </q-toolbar>

    <dynamic-table
      v-model:page="filterVals.page"
      :headers="tableHeaders"
      :list="results"
      :showing-results="filterVals.rows"
      :total-results="count"
      :loading="loading"
      :row-props="getRowProps"
      :group-func="item => item.is_priority ? 'common' : (item.code || '#')[0].toUpperCase()"
      anchor="dialog"
      separator
      clickable
      pagination
    >
      <template #group-name="{ group, listMode, rows }">
        <sticky-header
          v-if="group !== 'common'"
          static
          color="black"
        >
          {{ group }}
        </sticky-header>

        <q-item-wrapper
          v-else
          tag="label"
          dense
          label-class="dialog-heading text-bold"
          label="⭐ Common"
        >
          <template #before>
            <q-checkbox
              :model-value="hasSelectedAllInGroup(rows)"
              color="blue-grey"
              @update:model-value="selectAllPriorityItems($event, rows)"
            />
          </template>
        </q-item-wrapper>
      </template>

      <template #field-name="{ row }">
        <span class="module-name">
          {{ row.name }}
        </span>
      </template>

      <template #field-category="{ row }">
        <q-chip
          v-if="row.system_type"
          dense
          size="12px"
          class="q-ma-none"
          :style="{'background-color': changeColorBrightness(row.system_type.color, 89)}"
        >
          {{ row.system_type.name }}
        </q-chip>
      </template>

      <template #field-action="{ row }">
        <q-checkbox
          class="q-ml-sm"
          dense
          :model-value="isRowSelected(row)"
          @update:model-value="onSelectItem(row)"
        />
      </template>

      <template #row="{ row }">
        <DefaultItem
          :item="row"
          :active="isRowSelected(row)"
          clickable
          tag="label"
        >
          <template #before>
            <q-item-section side>
              <q-checkbox
                :model-value="isRowSelected(row)"
                @update:model-value="onSelectItem(row)"
              />
            </q-item-section>
          </template>
        </DefaultItem>
      </template>
    </dynamic-table>

    <template #bottom>
      <q-toolbar>
        <q-btn
          v-if="selectedItems.length > 0"
          :label="`Clear ${selectedItems.length} ${pluraliseString('item', selectedItems.length)}`"
          outline
          @click="selectedItems = []"
        />
        <q-space />
        <q-btn
          :disable="selectedItems.length < 1"
          label="Add Items"
          color="blue"
          icon-right="mdi-arrow-right"
          @click="confirmDialogOpen = true"
        />
      </q-toolbar>
    </template>
  </content-dialog>

  <message-dialog
    v-model="confirmDialogOpen"
    title="Confirm"
  >
    <q-item-label>
      Are you sure you want to add <b>{{ selectedItems.length }} {{ pluraliseString('item', selectedItems.length) }}</b> to your business inventory collection?
    </q-item-label>

    <template v-if="selectedItems.length > 0" #after>
      <q-expansion-item
        :label="`View Selected Items (${selectedItems.length})`"
        header-class="text-bold"
      >
        <q-list>
          <DefaultItem
            v-for="item of selectedItems"
            :key="item.id"
            :item="item"
            class="selected-item"
          >
            <template #after>
              <q-item-section side>
                <cancel-button
                  flat
                  selector=".selected-item"
                  hover-selector-class="bg-red-1"
                  @click="onSelectItem(item)"
                />
              </q-item-section>
            </template>
          </DefaultItem>
        </q-list>
      </q-expansion-item>
    </template>

    <template #actions>
      <q-btn
        v-close-popup
        flat
        color="black"
        label="Cancel"
      />
      <q-btn
        v-close-popup
        autofocus
        flat
        color="blue"
        label="Continue"
        :disable="selectedItems.length < 1"
        @click="addItemsToInventory"
      />
    </template>
  </message-dialog>
</template>

<script>
import DefaultItem from './DefaultItem'
import { performFilter } from 'components/app/inventory/defaults/filter'

export default {
  components: {
    DefaultItem
  },
  emits: ['added', 'createNew'],
  data () {
    return {
      filterVals: {
        page: 1,
        sortBy: 'priority',
        sortDirection: 'asc',
        search: '',
        rows: 50,
        system_types: []
      },
      tableHeaders: [
        { key: 'action', width: '30px', fixed: true },
        { key: 'code', label: 'Code', width: '20%' },
        { key: 'name', label: 'Name', width: '50%' },
        { key: 'category', label: 'Category', width: '20%' }
      ],
      loading: false,
      count: 0,
      options: null,
      results: [],
      selectedItems: [],
      dialogOpen: false,
      confirmDialogOpen: false
    }
  },
  computed: {
    selectedItemIds () {
      return this.selectedItems.map(item => item.id)
    }
  },
  watch: {
    'filterVals.search' () {
      if (this.filterVals.page !== 1) {
        this.filterVals.page = 1
      }
      else {
        this.performFilter()
      }
    },
    'filterVals.page' () {
      this.performFilter()
    },
    'filterVals.system_types' () {
      this.performFilter()
    }
  },
  methods: {
    open () {
      this.dialogOpen = true
      this.performFilter()
    },
    hide () {
      this.selectedItems = []
      this.dialogOpen = false
    },
    isRowSelected (row) {
      return !!this.selectedItems.find(item => item.id === row.id)
    },
    hasSelectedAllInGroup (rows) {
      const groupIds = rows.map(item => item.id)
      return this.selectedItemIds.filter(id => groupIds.includes(id)).length >= groupIds.length
    },
    selectAllPriorityItems (val, rows) {
      if (val) {
        this.selectedItems.push(...rows.filter(item => !this.selectedItemIds.includes(item.id)))
      }
      else {
        for (const row of rows) {
          const index = this.selectedItems.findIndex(selected => selected.id === row.id)
          if (index > -1) {
            this.selectedItems.splice(index, 1)
          }
        }
      }
    },
    getRowProps (row) {
      return {
        tag: 'label',
        dense: true,
        active: this.isRowSelected(row)
      }
    },
    async performFilter () {
      this.loading = true
      const { count, list, options } = await performFilter(
        this.filterVals.rows,
        this.filterVals.sortBy,
        this.filterVals.page,
        this.filterVals.search,
        null,
        this.filterVals.system_types
      )
      this.loading = false
      this.count = count
      this.results = list
      this.options = options
    },
    onSelectItem (val) {
      if (this.isRowSelected(val)) {
        this.selectedItems.splice(this.selectedItems.findIndex(item => item.id === val.id), 1)
      }
      else {
        this.selectedItems.push(val)
      }
    },
    async addItemsToInventory () {
      this.$loading.show()
      const { status, payload } = await this.$post('v1/inventory/defaults/company/add', {
        ids: this.selectedItemIds
      })
      this.$loading.hide()
      if (status === 'success') {
        const messages = [
          `${payload.success} inventory prefills have been added to your business inventory collection and are ready to use.`
        ]
        if (payload.failures.length > 0) {
          messages.push({
            labelClass: 'text-bold text-red-8',
            message: `${payload.failures.length} items failed to save`
          })
          messages.push(...payload.failures)
        }

        this.$q.dialog({
          title: 'Complete',
          message: messages
        })

        if (payload.success > 0) {
          this.$bus.emit('inventory:reload', true)
        }

        this.$emit('added')
        this.hide()
      }
    }
  }
}
</script>
